import { queryClient as defaultQueryClient } from '@/plugins/vueQuery';
import { queryKeys } from '../constants';
import type { QueryInvalidationParams } from '@/types/query';

const MODULE_KEY = 'users';

/** Query Key Getters */

export const getMeQueryKey = () => [MODULE_KEY, queryKeys.ME];

/** Independent of `getMeQueryKey`. Both `/me` versions fetch at session start. */
export const getMeV3QueryKey = () => [MODULE_KEY, queryKeys.ME_V3];

export const getUserPermissionsQueryKey = (userTwrn?: string | null) => {
  const key = [MODULE_KEY, queryKeys.USER_PERMISSIONS];

  if (userTwrn) {
    key.push(userTwrn);
  }

  return key;
};

export const getUserPermissionsListQueryKey = () => [MODULE_KEY, queryKeys.USER_PERMISSIONS_LIST];

/** Separate from the legacy list key: the v3 list has a different shape and endpoint. */
export const getUserPermissionsListV3QueryKey = () => [MODULE_KEY, queryKeys.USER_PERMISSIONS_LIST_V3];

/** One user's saved grants for the v3 edit drawer. Distinct from the legacy per-team detail key. */
export const getUserPermissionGrantsV3QueryKey = (userTwrn?: string | null) => {
  const key = [MODULE_KEY, queryKeys.USER_PERMISSION_GRANTS_V3];

  if (userTwrn) {
    key.push(userTwrn);
  }

  return key;
};

export const getNotificationSettingsQueryKey = (userTwrn?: string | null) => {
  const key = [MODULE_KEY, queryKeys.NOTIFICATION_SETTINGS];

  if (userTwrn) {
    key.push(userTwrn);
  }

  return key;
};

/** Invalidation Helpers */

export const invalidateUserPermissions = (
  userTwrn?: string | null,
  options?: QueryInvalidationParams,
) => {
  const client = options?.queryClient ?? defaultQueryClient;

  // The listing shows all users, so always invalidate it regardless of userTwrn.
  client.invalidateQueries({ queryKey: getUserPermissionsListQueryKey() });
  // With no userTwrn this is a broader prefix, invalidating every detail query.
  client.invalidateQueries({ queryKey: getUserPermissionsQueryKey(userTwrn) });
};

/**
 * Invalidate the v3 caches a save can change: the org users list and each affected user's
 * detail query. Targets the v3 keys only, so the legacy caches are left untouched.
 */
export const invalidateUserPermissionGrantsV3 = (
  userTwrns: string[] = [],
  options?: QueryInvalidationParams,
) => {
  const client = options?.queryClient ?? defaultQueryClient;

  // The v3 list shows every user, so always invalidate it regardless of the affected users.
  client.invalidateQueries({ queryKey: getUserPermissionsListV3QueryKey() });

  const affected = new Set(userTwrns);

  // Match the affected users' v3 detail queries in a single cache pass. With no users given,
  // every v3 detail query is invalidated.
  client.invalidateQueries({
    predicate: (query) => {
      const [module, key, userTwrn] = query.queryKey;

      return module === MODULE_KEY
        && key === queryKeys.USER_PERMISSION_GRANTS_V3
        && (affected.size === 0 || affected.has(userTwrn as string));
    },
  });
};
